import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { StatusPill } from "./StatusPill";
import { HeroVisual } from "./HeroVisual";
import { SectionBg } from "./SectionBg";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../lib/i18n";

export const Hero = ({ plant, totalStock, onOrder }) => {
  const { t, lang } = useLang();
  const { scrollY } = useScroll();
  const headY = useTransform(scrollY, [0, 700], [0, -80]);
  const util = plant ? Math.min(100, plant.week_utilization ?? 0) : null;

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] pt-[110px] pb-16 overflow-hidden">
      <SectionBg src="/images/paddy-field.jpg" position="center 60%" opacity={0.8} testId="hero-bg" />
      <div className="absolute inset-0 spotlight opacity-70 pointer-events-none" style={{ "--mx": "78%", "--my": "35%" }} />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: headY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>
            <StatusPill status={plant?.status} extra={util !== null && plant?.status === "running" ? t("hero.capacity", { n: util }) : null} />
          </motion.div>
          <h1 key={lang} className="mt-8 font-display font-bold tracking-[-0.04em] leading-[0.92] text-[15vw] sm:text-7xl lg:text-[7.4rem]">
            <MaskLines lines={[t("hero.l1"), t("hero.l2")]} delay={0.25} />
            <MaskLines lines={[<span key="f">{t("hero.pre")}<span className="font-serif-i font-normal text-[var(--husk)] tracking-normal">{t("hero.accent")}</span></span>]} delay={0.49} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[var(--sage)] leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-order-btn" onClick={() => onOrder()} className="group inline-flex items-center gap-3 rounded-full bg-[var(--husk)] text-[var(--forest)] pl-7 pr-2 py-2 font-semibold transition-transform hover:-translate-y-0.5">
              {t("cta.order")}
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--forest)] text-[var(--husk)] transition-transform group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </button>
            <button data-testid="hero-live-btn" onClick={() => scrollToId("live")} className="group inline-flex items-center gap-3 rounded-full border hairline px-7 py-4 text-[var(--cream)] hover:bg-white/5 transition-colors">
              <span className="font-mono text-sm" data-testid="hero-total-stock">{totalStock.toLocaleString("en-IN")} t</span> {t("hero.stock")}
              <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 h-[420px] sm:h-[540px] lg:h-[620px]">
          <HeroVisual scrollY={scrollY} />
        </div>
      </div>
    </section>
  );
};
